import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux';
import { persistReducer, persistStore } from 'redux-persist';
import { thunk } from 'redux-thunk';
import { analyticsMiddleware } from '~shared/lib/analytics/src/middleware';
import sentryReduxEnhancerConfig from '~shared/lib/sentryReduxEnhancerConfig';
import cart from '~shared/middlewares/cart';
import { reduxStorage } from '~shared/mmkv';
import { queryClient } from '~shared/react-query/queryClient';
import reactors from '~shared/reactors';
import { Sentry } from '~shared/sentry';
import reducersWithLogout from './reducer';

const persistConfig = {
  key: 'root',
  storage: reduxStorage,
  blacklist: ['form', 'routing', 'modal', 'analytics', 'appTime', 'ui', 'presence'],
};

/**
 * Sentry's createReduxEnhancer registers a global event processor on every createStore() call.
 * Callers must not create a new store per render or per action (e.g. persistor.purge), or
 * processors accumulate until notifyEventProcessors overflows the stack.
 */
let storeSingleton: ReturnType<typeof createStoreOnce> | undefined;

function createStoreOnce() {
  // @ts-expect-error TS(2339): Property '__REDUX_DEVTOOLS_EXTENSION_COMPOSE__' does not exist on type 'Window & typeof globalThis'.
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  const persistedReducer = persistReducer(persistConfig, reducersWithLogout);

  const store = createStore(
    persistedReducer,
    composeEnhancers(
      applyMiddleware(thunk, cart(queryClient), analyticsMiddleware),
      Sentry.createReduxEnhancer(sentryReduxEnhancerConfig),
    ),
  );

  const persistor = persistStore(store);

  if (module.hot) {
    module.hot.accept('./reducer', () => {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const nextReducer = require('./reducer').default;

      store.replaceReducer(nextReducer);
    });
  }

  reactors(store);

  return { store, persistor };
}

export const configureStore = () => {
  storeSingleton ??= createStoreOnce();
  return storeSingleton;
};
